import { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  MarkerType,
  Handle,
  Position,
  NodeProps,
  BackgroundVariant,
  Panel,
  useReactFlow,
  useViewport,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Square,
  Circle,
  Diamond,
  RectangleHorizontal,
  Trash2,
  Check,
  X,
  Download,
  ArrowRight,
  GitBranch,
  Users,
  Network,
  MousePointer2,
  Pen,
  Eraser,
  Undo2,
  Maximize2,
  Minimize2,
  MoveRight,
  Minus,
  Spline,
  CornerDownRight,
} from 'lucide-react';

interface Stroke {
  id: string;
  points: { x: number; y: number }[];
  color: string;
  width: number;
  type: 'freehand' | 'arrow' | 'line';
}

interface StrokeLayerProps {
  strokes: Stroke[];
}

function StrokeLayer({ strokes }: StrokeLayerProps) {
  const viewport = useViewport();
  
  const renderArrowhead = (stroke: Stroke, scaledWidth: number) => {
    if (stroke.points.length < 2) return null;
    const lastPoint = stroke.points[stroke.points.length - 1];
    const prevPoint = stroke.points[stroke.points.length - 2];
    const angle = Math.atan2(lastPoint.y - prevPoint.y, lastPoint.x - prevPoint.x);
    const arrowLength = Math.max(12, scaledWidth * 4);
    const arrowAngle = Math.PI / 6;
    
    const x1 = lastPoint.x - arrowLength * Math.cos(angle - arrowAngle);
    const y1 = lastPoint.y - arrowLength * Math.sin(angle - arrowAngle);
    const x2 = lastPoint.x - arrowLength * Math.cos(angle + arrowAngle);
    const y2 = lastPoint.y - arrowLength * Math.sin(angle + arrowAngle);
    
    return (
      <polygon
        points={`${lastPoint.x},${lastPoint.y} ${x1},${y1} ${x2},${y2}`}
        fill={stroke.color}
      />
    );
  };
  
  return (
    <svg
      className="react-flow__strokes absolute inset-0 pointer-events-none"
      style={{ zIndex: 0, overflow: 'visible' }}
    >
      <g transform={`translate(${viewport.x}, ${viewport.y}) scale(${viewport.zoom})`}>
        {strokes.map((stroke) => {
          if (stroke.points.length < 2) return null;
          const scaledWidth = stroke.width / viewport.zoom;
          
          if (stroke.type === 'line' || stroke.type === 'arrow') {
            const start = stroke.points[0];
            const end = stroke.points[stroke.points.length - 1];
            return (
              <g key={stroke.id}>
                <line
                  x1={start.x}
                  y1={start.y}
                  x2={end.x}
                  y2={end.y}
                  stroke={stroke.color}
                  strokeWidth={scaledWidth}
                  strokeLinecap="round"
                />
                {stroke.type === 'arrow' && renderArrowhead(stroke, scaledWidth)}
              </g>
            );
          }
          
          const pathData = stroke.points.reduce((acc, point, i) => {
            if (i === 0) return `M ${point.x} ${point.y}`;
            return `${acc} L ${point.x} ${point.y}`;
          }, '');
          return (
            <path
              key={stroke.id}
              d={pathData}
              stroke={stroke.color}
              strokeWidth={scaledWidth}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        })}
      </g>
    </svg>
  );
}

interface DiagramCanvasProps {
  initialData?: string;
  onSave?: (data: string) => void;
  onCancel?: () => void;
  readOnly?: boolean;
  isFullscreen?: boolean;
  onFullscreenChange?: (fullscreen: boolean) => void;
  onAutoSave?: (data: string) => void;
}

interface CustomNodeData extends Record<string, unknown> {
  label: string;
  shapeType: 'rectangle' | 'circle' | 'diamond' | 'rounded';
  color: string;
  textColor: string;
}

const SHAPE_COLORS = [
  { name: 'Blue', value: '#3B82F6', text: '#FFFFFF' },
  { name: 'Green', value: '#10B981', text: '#FFFFFF' },
  { name: 'Red', value: '#EF4444', text: '#FFFFFF' },
  { name: 'Yellow', value: '#FACC15', text: '#000000' },
  { name: 'Purple', value: '#A855F7', text: '#FFFFFF' },
  { name: 'Orange', value: '#F97316', text: '#FFFFFF' },
  { name: 'Gray', value: '#6B7280', text: '#FFFFFF' },
  { name: 'White', value: '#FFFFFF', text: '#000000' },
];

function RectangleNode({ data, selected }: NodeProps<Node<CustomNodeData>>) {
  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(data.label);

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    data.label = label;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
      data.label = label;
    }
  };

  return (
    <div
      className={`px-4 py-2 shadow-md border-2 min-w-[100px] text-center ${
        selected ? 'border-primary ring-2 ring-primary/20' : 'border-gray-300'
      }`}
      style={{ backgroundColor: data.color, color: data.textColor }}
      onDoubleClick={handleDoubleClick}
    >
      <Handle type="target" position={Position.Top} className="!bg-gray-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-gray-500" />
      <Handle type="target" position={Position.Left} className="!bg-gray-500" />
      <Handle type="source" position={Position.Right} className="!bg-gray-500" />
      {isEditing ? (
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className="text-center bg-transparent border-none p-0 h-auto text-inherit"
        />
      ) : (
        <span className="font-medium">{data.label}</span>
      )}
    </div>
  );
}

function RoundedNode({ data, selected }: NodeProps<Node<CustomNodeData>>) {
  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(data.label);

  const handleDoubleClick = () => setIsEditing(true);
  const handleBlur = () => { setIsEditing(false); data.label = label; };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { setIsEditing(false); data.label = label; }
  };

  return (
    <div
      className={`px-4 py-2 shadow-md border-2 rounded-xl min-w-[100px] text-center ${
        selected ? 'border-primary ring-2 ring-primary/20' : 'border-gray-300'
      }`}
      style={{ backgroundColor: data.color, color: data.textColor }}
      onDoubleClick={handleDoubleClick}
    >
      <Handle type="target" position={Position.Top} className="!bg-gray-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-gray-500" />
      <Handle type="target" position={Position.Left} className="!bg-gray-500" />
      <Handle type="source" position={Position.Right} className="!bg-gray-500" />
      {isEditing ? (
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className="text-center bg-transparent border-none p-0 h-auto text-inherit"
        />
      ) : (
        <span className="font-medium">{data.label}</span>
      )}
    </div>
  );
}

function CircleNode({ data, selected }: NodeProps<Node<CustomNodeData>>) {
  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(data.label);

  const handleDoubleClick = () => setIsEditing(true);
  const handleBlur = () => { setIsEditing(false); data.label = label; };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { setIsEditing(false); data.label = label; }
  };

  return (
    <div
      className={`w-24 h-24 rounded-full shadow-md border-2 flex items-center justify-center ${
        selected ? 'border-primary ring-2 ring-primary/20' : 'border-gray-300'
      }`}
      style={{ backgroundColor: data.color, color: data.textColor }}
      onDoubleClick={handleDoubleClick}
    >
      <Handle type="target" position={Position.Top} className="!bg-gray-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-gray-500" />
      <Handle type="target" position={Position.Left} className="!bg-gray-500" />
      <Handle type="source" position={Position.Right} className="!bg-gray-500" />
      {isEditing ? (
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className="text-center bg-transparent border-none p-0 h-auto text-inherit w-20"
        />
      ) : (
        <span className="font-medium text-center text-sm px-1">{data.label}</span>
      )}
    </div>
  );
}

function DiamondNode({ data, selected }: NodeProps<Node<CustomNodeData>>) {
  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(data.label);

  const handleDoubleClick = () => setIsEditing(true);
  const handleBlur = () => { setIsEditing(false); data.label = label; };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { setIsEditing(false); data.label = label; }
  };

  return (
    <div
      className={`w-28 h-28 shadow-md border-2 flex items-center justify-center ${
        selected ? 'border-primary ring-2 ring-primary/20' : 'border-gray-300'
      }`}
      style={{
        backgroundColor: data.color,
        color: data.textColor,
        transform: 'rotate(45deg)',
      }}
      onDoubleClick={handleDoubleClick}
    >
      <Handle type="target" position={Position.Top} className="!bg-gray-500" style={{ transform: 'rotate(-45deg)' }} />
      <Handle type="source" position={Position.Bottom} className="!bg-gray-500" style={{ transform: 'rotate(-45deg)' }} />
      <Handle type="target" position={Position.Left} className="!bg-gray-500" style={{ transform: 'rotate(-45deg)' }} />
      <Handle type="source" position={Position.Right} className="!bg-gray-500" style={{ transform: 'rotate(-45deg)' }} />
      <div style={{ transform: 'rotate(-45deg)' }}>
        {isEditing ? (
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus
            className="text-center bg-transparent border-none p-0 h-auto text-inherit w-16"
          />
        ) : (
          <span className="font-medium text-center text-sm">{data.label}</span>
        )}
      </div>
    </div>
  );
}

const nodeTypes = {
  rectangle: RectangleNode,
  rounded: RoundedNode,
  circle: CircleNode,
  diamond: DiamondNode,
};

const initialNodes: Node<CustomNodeData>[] = [];
const initialEdges: Edge[] = [];

const STROKE_COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Green', value: '#10B981' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Purple', value: '#A855F7' },
];

const STROKE_WIDTHS = [2, 4, 6, 8];

type ToolMode = 'select' | 'draw' | 'arrow' | 'line';

function DiagramCanvasInner({
  initialData,
  onSave,
  onCancel,
  readOnly = false,
  isFullscreen: externalFullscreen,
  onFullscreenChange,
  onAutoSave,
}: DiagramCanvasProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<CustomNodeData>>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [selectedColor, setSelectedColor] = useState(SHAPE_COLORS[0]);
  const [edgeType, setEdgeType] = useState<'default' | 'step' | 'smoothstep' | 'straight'>('smoothstep');
  const nodeIdCounter = useRef(1);
  const strokeIdCounter = useRef(1);
  const hasLoadedInitialData = useRef(false);
  
  const [toolMode, setToolMode] = useState<ToolMode>('select');
  const [strokeColor, setStrokeColor] = useState(STROKE_COLORS[0].value);
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [isDrawing, setIsDrawing] = useState(false);
  const currentStroke = useRef<Stroke | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  
  // Use external fullscreen state if provided (for Tiptap persistence), otherwise use local state
  // When externalFullscreen is provided, this component is "controlled" and must use that value
  const [localFullscreen, setLocalFullscreen] = useState(false);
  
  // If external prop is provided, always use it (controlled mode)
  // This ensures fullscreen state survives Tiptap remounts
  const isFullscreen = externalFullscreen !== undefined ? externalFullscreen : localFullscreen;
  
  const { screenToFlowPosition, fitView } = useReactFlow();

  // Use CSS-only fullscreen to avoid issues with Radix portals
  const toggleFullscreen = useCallback(() => {
    const newValue = !isFullscreen;
    // If controlled, only notify parent - don't update local state
    if (externalFullscreen !== undefined) {
      onFullscreenChange?.(newValue);
    } else {
      setLocalFullscreen(newValue);
    }
    setTimeout(() => fitView(), 100);
  }, [fitView, isFullscreen, externalFullscreen, onFullscreenChange]);

  useEffect(() => {
    if (hasLoadedInitialData.current) return;
    hasLoadedInitialData.current = true;
    
    if (initialData) {
      try {
        const parsed = JSON.parse(initialData);
        if (parsed.nodes) setNodes(parsed.nodes);
        if (parsed.edges) setEdges(parsed.edges);
        if (parsed.strokes) setStrokes(parsed.strokes);
        if (parsed.nodeIdCounter) nodeIdCounter.current = parsed.nodeIdCounter;
        if (parsed.strokeIdCounter) strokeIdCounter.current = parsed.strokeIdCounter;
      } catch (error) {
        console.error('Failed to load diagram data:', error);
      }
    }
  }, [initialData, setNodes, setEdges]);

  useEffect(() => {
    if (!onAutoSave || !hasLoadedInitialData.current) return;
    const data = JSON.stringify({
      nodes: nodes.map(n => ({ ...n, data: { ...n.data, label: n.data.label } })),
      edges,
      strokes,
      nodeIdCounter: nodeIdCounter.current,
      strokeIdCounter: strokeIdCounter.current,
    });
    onAutoSave(data);
  }, [nodes, edges, strokes, onAutoSave]);

  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge: Edge = {
        ...params,
        id: `e${params.source}-${params.target}`,
        type: edgeType,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
        },
        style: { strokeWidth: 2 },
      } as Edge;
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges, edgeType]
  );

  const addShape = (shapeType: 'rectangle' | 'circle' | 'diamond' | 'rounded') => {
    const newNode: Node<CustomNodeData> = {
      id: `node-${nodeIdCounter.current++}`,
      type: shapeType,
      position: { x: 100 + Math.random() * 200, y: 100 + Math.random() * 200 },
      data: {
        label: shapeType === 'diamond' ? 'Decision' : 'New Node',
        shapeType,
        color: selectedColor.value,
        textColor: selectedColor.text,
      },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const deleteSelected = () => {
    setNodes((nds) => nds.filter((n) => !n.selected));
    setEdges((eds) => eds.filter((e) => !e.selected));
  };

  const handleSave = () => {
    const currentNodes = nodes.map(n => ({
      ...n,
      data: { ...n.data, label: n.data.label }
    }));
    const data = {
      nodes: currentNodes,
      edges,
      strokes,
      nodeIdCounter: nodeIdCounter.current,
      strokeIdCounter: strokeIdCounter.current,
    };
    onSave?.(JSON.stringify(data));
  };

  const isDrawingMode = toolMode === 'draw' || toolMode === 'arrow' || toolMode === 'line';

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!isDrawingMode || readOnly) return;
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const flowPosition = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    
    const strokeType: 'freehand' | 'arrow' | 'line' = 
      toolMode === 'arrow' ? 'arrow' : 
      toolMode === 'line' ? 'line' : 'freehand';
    
    currentStroke.current = {
      id: `stroke-${strokeIdCounter.current++}`,
      points: [flowPosition],
      color: strokeColor,
      width: strokeWidth,
      type: strokeType,
    };
    setIsDrawing(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [toolMode, isDrawingMode, readOnly, screenToFlowPosition, strokeColor, strokeWidth]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDrawing || !currentStroke.current || !isDrawingMode) return;
    
    const flowPosition = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    
    if (currentStroke.current.type === 'arrow' || currentStroke.current.type === 'line') {
      currentStroke.current.points = [currentStroke.current.points[0], flowPosition];
    } else {
      currentStroke.current.points.push(flowPosition);
    }
    
    setStrokes(prev => {
      const existing = prev.filter(s => s.id !== currentStroke.current!.id);
      return [...existing, { ...currentStroke.current! }];
    });
  }, [isDrawing, isDrawingMode, screenToFlowPosition]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDrawing || !currentStroke.current) return;
    
    if (currentStroke.current.points.length >= 2) {
      setStrokes(prev => {
        const existing = prev.filter(s => s.id !== currentStroke.current!.id);
        return [...existing, { ...currentStroke.current! }];
      });
    }
    
    currentStroke.current = null;
    setIsDrawing(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  }, [isDrawing]);

  const undoLastStroke = useCallback(() => {
    setStrokes(prev => prev.slice(0, -1));
  }, []);

  const clearAllStrokes = useCallback(() => {
    setStrokes([]);
  }, []);

  const handleExport = () => {
    const data = { nodes, edges };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `diagram-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const addOrgChartTemplate = () => {
    const baseX = 300;
    const baseY = 50;
    const newNodes: Node<CustomNodeData>[] = [
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rounded',
        position: { x: baseX, y: baseY },
        data: { label: 'CEO', shapeType: 'rounded', color: '#3B82F6', textColor: '#FFFFFF' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rounded',
        position: { x: baseX - 150, y: baseY + 100 },
        data: { label: 'Manager 1', shapeType: 'rounded', color: '#10B981', textColor: '#FFFFFF' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rounded',
        position: { x: baseX + 150, y: baseY + 100 },
        data: { label: 'Manager 2', shapeType: 'rounded', color: '#10B981', textColor: '#FFFFFF' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rectangle',
        position: { x: baseX - 200, y: baseY + 200 },
        data: { label: 'Employee 1', shapeType: 'rectangle', color: '#FACC15', textColor: '#000000' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rectangle',
        position: { x: baseX - 100, y: baseY + 200 },
        data: { label: 'Employee 2', shapeType: 'rectangle', color: '#FACC15', textColor: '#000000' },
      },
    ];
    
    const startId = nodeIdCounter.current - 5;
    const newEdges: Edge[] = [
      { id: `e${startId}-${startId + 1}`, source: `node-${startId}`, target: `node-${startId + 1}`, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },
      { id: `e${startId}-${startId + 2}`, source: `node-${startId}`, target: `node-${startId + 2}`, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },
      { id: `e${startId + 1}-${startId + 3}`, source: `node-${startId + 1}`, target: `node-${startId + 3}`, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },
      { id: `e${startId + 1}-${startId + 4}`, source: `node-${startId + 1}`, target: `node-${startId + 4}`, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },
    ];
    
    setNodes((nds) => [...nds, ...newNodes]);
    setEdges((eds) => [...eds, ...newEdges]);
  };

  const addFlowchartTemplate = () => {
    const baseX = 300;
    const baseY = 50;
    const newNodes: Node<CustomNodeData>[] = [
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rounded',
        position: { x: baseX, y: baseY },
        data: { label: 'Start', shapeType: 'rounded', color: '#10B981', textColor: '#FFFFFF' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rectangle',
        position: { x: baseX, y: baseY + 100 },
        data: { label: 'Process 1', shapeType: 'rectangle', color: '#3B82F6', textColor: '#FFFFFF' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'diamond',
        position: { x: baseX - 14, y: baseY + 180 },
        data: { label: 'Decision?', shapeType: 'diamond', color: '#FACC15', textColor: '#000000' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rectangle',
        position: { x: baseX - 150, y: baseY + 320 },
        data: { label: 'Yes Path', shapeType: 'rectangle', color: '#3B82F6', textColor: '#FFFFFF' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rectangle',
        position: { x: baseX + 150, y: baseY + 320 },
        data: { label: 'No Path', shapeType: 'rectangle', color: '#3B82F6', textColor: '#FFFFFF' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rounded',
        position: { x: baseX, y: baseY + 420 },
        data: { label: 'End', shapeType: 'rounded', color: '#EF4444', textColor: '#FFFFFF' },
      },
    ];
    
    const startId = nodeIdCounter.current - 6;
    const newEdges: Edge[] = [
      { id: `e${startId}-${startId + 1}`, source: `node-${startId}`, target: `node-${startId + 1}`, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },
      { id: `e${startId + 1}-${startId + 2}`, source: `node-${startId + 1}`, target: `node-${startId + 2}`, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },
      { id: `e${startId + 2}-${startId + 3}`, source: `node-${startId + 2}`, target: `node-${startId + 3}`, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },
      { id: `e${startId + 2}-${startId + 4}`, source: `node-${startId + 2}`, target: `node-${startId + 4}`, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },
      { id: `e${startId + 3}-${startId + 5}`, source: `node-${startId + 3}`, target: `node-${startId + 5}`, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },
      { id: `e${startId + 4}-${startId + 5}`, source: `node-${startId + 4}`, target: `node-${startId + 5}`, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },
    ];
    
    setNodes((nds) => [...nds, ...newNodes]);
    setEdges((eds) => [...eds, ...newEdges]);
  };

  const addMindMapTemplate = () => {
    const baseX = 400;
    const baseY = 200;
    const newNodes: Node<CustomNodeData>[] = [
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'circle',
        position: { x: baseX, y: baseY },
        data: { label: 'Main Idea', shapeType: 'circle', color: '#A855F7', textColor: '#FFFFFF' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rounded',
        position: { x: baseX - 200, y: baseY - 100 },
        data: { label: 'Topic 1', shapeType: 'rounded', color: '#3B82F6', textColor: '#FFFFFF' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rounded',
        position: { x: baseX + 150, y: baseY - 100 },
        data: { label: 'Topic 2', shapeType: 'rounded', color: '#10B981', textColor: '#FFFFFF' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rounded',
        position: { x: baseX - 200, y: baseY + 100 },
        data: { label: 'Topic 3', shapeType: 'rounded', color: '#F97316', textColor: '#FFFFFF' },
      },
      {
        id: `node-${nodeIdCounter.current++}`,
        type: 'rounded',
        position: { x: baseX + 150, y: baseY + 100 },
        data: { label: 'Topic 4', shapeType: 'rounded', color: '#EF4444', textColor: '#FFFFFF' },
      },
    ];
    
    const startId = nodeIdCounter.current - 5;
    const newEdges: Edge[] = [
      { id: `e${startId}-${startId + 1}`, source: `node-${startId}`, target: `node-${startId + 1}`, type: 'smoothstep' },
      { id: `e${startId}-${startId + 2}`, source: `node-${startId}`, target: `node-${startId + 2}`, type: 'smoothstep' },
      { id: `e${startId}-${startId + 3}`, source: `node-${startId}`, target: `node-${startId + 3}`, type: 'smoothstep' },
      { id: `e${startId}-${startId + 4}`, source: `node-${startId}`, target: `node-${startId + 4}`, type: 'smoothstep' },
    ];
    
    setNodes((nds) => [...nds, ...newNodes]);
    setEdges((eds) => [...eds, ...newEdges]);
  };

  return (
    <div 
      ref={wrapperRef}
      className={`diagram-canvas-wrapper flex flex-col border rounded-lg overflow-hidden bg-white dark:bg-gray-900 ${isFullscreen ? 'fixed inset-0 z-50 h-screen w-screen rounded-none' : ''}`} 
      style={{ height: isFullscreen ? '100vh' : '500px' }}
    >
            {!readOnly && (
        <div 
          className="diagram-toolbar flex flex-wrap items-center gap-2 p-2 border-b bg-gray-50 dark:bg-gray-800"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1 bg-gray-200 dark:bg-gray-700 rounded p-0.5">
            <Button 
              variant={toolMode === 'select' ? 'default' : 'ghost'} 
              size="sm" 
              onClick={() => setToolMode('select')} 
              title="Select & Move"
              className="h-7"
            >
              <MousePointer2 className="w-4 h-4" />
            </Button>
            <Button 
              variant={toolMode === 'draw' ? 'default' : 'ghost'} 
              size="sm" 
              onClick={() => setToolMode('draw')} 
              title="Draw Freehand"
              className="h-7"
            >
              <Pen className="w-4 h-4" />
            </Button>
            <Button 
              variant={toolMode === 'arrow' ? 'default' : 'ghost'} 
              size="sm" 
              onClick={() => setToolMode('arrow')} 
              title="Draw Arrow"
              className="h-7"
            >
              <MoveRight className="w-4 h-4" />
            </Button>
            <Button 
              variant={toolMode === 'line' ? 'default' : 'ghost'} 
              size="sm" 
              onClick={() => setToolMode('line')} 
              title="Draw Line"
              className="h-7"
            >
              <Minus className="w-4 h-4" />
            </Button>
          </div>

          <Separator orientation="vertical" className="h-6" />

          {isDrawingMode ? (
            <>
              <span className="text-xs text-muted-foreground font-medium">
                {toolMode === 'arrow' ? 'Arrow:' : toolMode === 'line' ? 'Line:' : 'Pen:'}
              </span>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: strokeColor }} />
                <select 
                  value={strokeColor}
                  onChange={(e) => setStrokeColor(e.target.value)}
                  className="h-8 px-2 text-xs bg-transparent border rounded cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid="select-stroke-color"
                >
                  {STROKE_COLORS.map((color) => (
                    <option key={color.value} value={color.value}>{color.name}</option>
                  ))}
                </select>
              </div>
              <select 
                value={String(strokeWidth)}
                onChange={(e) => setStrokeWidth(Number(e.target.value))}
                className="h-8 px-2 text-xs bg-transparent border rounded cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="select-stroke-width"
              >
                {STROKE_WIDTHS.map((w) => (
                  <option key={w} value={String(w)}>{w}px</option>
                ))}
              </select>
              <Button variant="ghost" size="sm" onClick={undoLastStroke} title="Undo Last Stroke" disabled={strokes.length === 0}>
                <Undo2 className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={clearAllStrokes} title="Clear All Strokes" disabled={strokes.length === 0}>
                <Eraser className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <span className="text-xs text-muted-foreground font-medium">Shapes:</span>
              <Button variant="ghost" size="sm" onClick={() => addShape('rectangle')} title="Rectangle" data-testid="button-shape-rectangle">
                <Square className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => addShape('rounded')} title="Rounded Rectangle">
                <RectangleHorizontal className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => addShape('circle')} title="Circle" data-testid="button-shape-circle">
                <Circle className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => addShape('diamond')} title="Diamond (Decision)" data-testid="button-shape-diamond">
                <Diamond className="w-4 h-4" />
              </Button>

              <Separator orientation="vertical" className="h-6" />

              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded border" style={{ backgroundColor: selectedColor.value }} />
                <select 
                  value={selectedColor.value}
                  onChange={(e) => {
                    const color = SHAPE_COLORS.find(c => c.value === e.target.value);
                    if (color) setSelectedColor(color);
                  }}
                  className="h-8 px-2 text-xs bg-transparent border rounded cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid="select-color"
                >
                  {SHAPE_COLORS.map((color) => (
                    <option key={color.value} value={color.value}>{color.name}</option>
                  ))}
                </select>
              </div>

              <Separator orientation="vertical" className="h-6" />

              <span className="text-xs text-muted-foreground font-medium">Connectors:</span>
              <select 
                value={edgeType}
                onChange={(e) => setEdgeType(e.target.value as typeof edgeType)}
                className="h-8 px-2 text-xs bg-transparent border rounded cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="select-connector"
              >
                <option value="smoothstep">Curved</option>
                <option value="step">Stepped</option>
                <option value="straight">Straight</option>
              </select>

              <Separator orientation="vertical" className="h-6" />

              <span className="text-xs text-muted-foreground font-medium">Templates:</span>
              <Button variant="ghost" size="sm" onClick={addOrgChartTemplate} title="Org Chart Template">
                <Users className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={addFlowchartTemplate} title="Flowchart Template">
                <GitBranch className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={addMindMapTemplate} title="Mind Map Template">
                <Network className="w-4 h-4" />
              </Button>
            </>
          )}

          <Separator orientation="vertical" className="h-6" />

          <Button variant="ghost" size="sm" onClick={deleteSelected} title="Delete Selected">
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleExport} title="Export JSON">
            <Download className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={toggleFullscreen} 
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            data-testid="diagram-fullscreen-toggle"
            aria-label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>

          {onSave && onCancel && (
            <>
              <Separator orientation="vertical" className="h-6" />
              <Button variant="default" size="sm" onClick={handleSave} title="Save Diagram">
                <Check className="w-4 h-4 mr-1" />
                Save
              </Button>
              <Button variant="outline" size="sm" onClick={onCancel} title="Cancel">
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
            </>
          )}
        </div>
      )}

      <div 
        ref={canvasRef}
        className="relative flex-1"
        style={{ cursor: isDrawingMode ? 'crosshair' : 'default' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={readOnly ? undefined : onNodesChange}
          onEdgesChange={readOnly ? undefined : onEdgesChange}
          onConnect={readOnly ? undefined : onConnect}
          nodeTypes={nodeTypes}
          fitView
          nodesDraggable={!readOnly && !isDrawingMode}
          nodesConnectable={!readOnly && !isDrawingMode}
          elementsSelectable={!readOnly && !isDrawingMode}
          panOnDrag={!readOnly && !isDrawingMode}
          zoomOnScroll={!readOnly}
          zoomOnPinch={!readOnly && !isDrawingMode}
          zoomOnDoubleClick={!readOnly && !isDrawingMode}
          defaultEdgeOptions={{
            type: edgeType,
            markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20 },
            style: { strokeWidth: 2 },
          }}
          proOptions={{ hideAttribution: true }}
        >
          <StrokeLayer strokes={strokes} />
          <Controls showInteractive={!readOnly} />
          <MiniMap zoomable={!readOnly} pannable={!readOnly} />
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          {!readOnly && (
            <Panel position="bottom-center" className="bg-white/80 dark:bg-gray-800/80 p-2 rounded shadow text-xs text-muted-foreground">
              {toolMode === 'draw' ? 'Draw freehand on canvas' : 
               toolMode === 'arrow' ? 'Click and drag to draw an arrow' :
               toolMode === 'line' ? 'Click and drag to draw a line' :
               'Drag from handles to connect • Double-click to edit text'}
            </Panel>
          )}
        </ReactFlow>
      </div>
    </div>
  );
}

export function DiagramCanvas(props: DiagramCanvasProps) {
  return (
    <ReactFlowProvider>
      <DiagramCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
